import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import { hasSeenTutorial as checkTutorialSeen, hasSeenTutorialSync, markTutorialAsSeen as saveTutorialSeen } from '../helpers/tutorialStorage';
import type { UseTutorialReturn } from '../types/tutorial.types';

/**
 * Hook to manage tutorial state
 * Supports global mode (tutorial shown before authentication)
 * On Web, initializes synchronously from localStorage for instant check (no loading)
 * On Mobile, checks asynchronously from AsyncStorage
 * 
 * @param userId - Optional user ID (deprecated, kept for backward compatibility)
 *                 When null or undefined, uses global storage (for pre-auth tutorial check)
 */
export function useTutorial(_userId?: string | null): UseTutorialReturn {
  // Web: Initialize synchronously from localStorage (instant, no loading)
  // Mobile: Initialize as false, will check async in useEffect
  const getInitialTutorialState = (): boolean => {
    if (Platform.OS === 'web') {
      return hasSeenTutorialSync();
    }
    return false; // Mobile will check async
  };

  const [hasSeenTutorial, setHasSeenTutorial] = useState<boolean>(getInitialTutorialState);
  // Web: No loading needed (sync initialization)
  // Mobile: Start with loading true, will set to false after async check
  const [isLoading, setIsLoading] = useState<boolean>(Platform.OS !== 'web');
  
  // Track mount state to prevent state updates after unmount
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;

    // On Web, we already have the value from sync initialization
    // Only need to check async on Mobile
    if (Platform.OS === 'web') {
      setIsLoading(false);
      return;
    }

    // Mobile: Check asynchronously
    const checkTutorial = async () => {
      try {
        const seen = await checkTutorialSeen();
        if (isMountedRef.current) {
          setHasSeenTutorial(seen);
        }
      } catch {
        if (isMountedRef.current) {
          setHasSeenTutorial(false);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    checkTutorial();

    // Cleanup: mark as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, []); // No dependencies - global check, doesn't depend on userId

  const markAsSeen = useCallback(async (): Promise<void> => {
    try {
      await saveTutorialSeen();
      setHasSeenTutorial(true);
    } catch {
      // Silently fail
    }
  }, []);

  return {
    hasSeenTutorial,
    isLoading,
    markAsSeen,
  };
}

